import type { ProductionScheduleItem, ProductionScriptCue } from "@/types/productionBrief.type";
import { parseScheduleTiming } from "../productionBriefSchedule";
import { scriptAddsLines } from "./briefParts";
import { BriefSection, BulletList, FieldLabel, SectionHead } from "./BriefPrimitives";
import { LegalLines, QuietTable, type QuietColumn } from "./BriefStaticSections";

function clean(items?: string[] | null): string[] {
  return (items ?? []).map((item) => item?.trim()).filter((item): item is string => !!item);
}

/**
 * The script, after the shot list. Shown only when at least one cue says
 * something the shots' voiceovers do not; then it is shown whole, in order.
 */
export function BriefScript({
  script,
  voiceovers,
}: {
  script?: ProductionScriptCue[];
  voiceovers: Array<string | null | undefined>;
}) {
  const cues = (script ?? []).filter((cue) => cue.line?.trim());
  if (!cues.length || !scriptAddsLines(cues, voiceovers)) return null;
  const hasTimecodes = cues.some((cue) => cue.timecode?.trim());
  return (
    <BriefSection accent="amber">
      <SectionHead accent="amber" title="Voiceover, script and sound" sub={`${cues.length} ${cues.length === 1 ? "line" : "lines"}`} />
      <div>
        {cues.map((cue, index) => (
          <div
            key={cue.id ?? `${cue.timecode}-${index}`}
            className={
              hasTimecodes
                ? "grid grid-cols-[76px_minmax(0,1fr)] gap-x-7 border-t border-co-line py-3 first:border-t-0 first:pt-0"
                : "border-t border-co-line py-3 first:border-t-0 first:pt-0"
            }
          >
            {hasTimecodes ? <span className="text-[13.5px] tabular-nums text-co-muted">{cue.timecode?.trim()}</span> : null}
            <p className="whitespace-pre-wrap text-[15px] leading-[1.45] text-co-text">{cue.line.trim()}</p>
          </div>
        ))}
      </div>
    </BriefSection>
  );
}

const SCHEDULE_COLUMNS: QuietColumn<ProductionScheduleItem>[] = [
  {
    key: "time",
    label: "Time",
    width: 140,
    value: (row) => {
      const timing = parseScheduleTiming(row);
      if (timing.start && timing.end) return `${timing.start} to ${timing.end}`;
      return timing.start || timing.legacyNote;
    },
  },
  {
    key: "length",
    label: "Length",
    width: 100,
    value: (row) => {
      const minutes = parseScheduleTiming(row).durationMinutes;
      return minutes ? `${minutes} min` : "";
    },
  },
  { key: "activity", label: "Activity", width: "fill", value: (row) => row.activity },
  { key: "owner", label: "Owner", width: 180, value: (row) => row.owner },
];

/** The shoot day as a quiet table: only the columns that carry data. */
export function BriefShootDay({ schedule }: { schedule?: ProductionScheduleItem[] }) {
  const rows = schedule ?? [];
  const filled = rows.filter((row) => SCHEDULE_COLUMNS.some((col) => col.value(row)?.trim()));
  if (!filled.length) return null;
  return (
    <BriefSection accent="blue">
      <SectionHead accent="blue" title="Shoot day" />
      <QuietTable
        label="Shoot day"
        columns={SCHEDULE_COLUMNS}
        rows={filled}
        rowKey={(row, index) => row.id ?? `${row.startTime ?? row.time ?? ""}-${index}`}
      />
    </BriefSection>
  );
}

/** What must not change (the concept lock) and the legal line. */
export function BriefRules({ conceptLock, legal }: { conceptLock?: string[]; legal?: string[] }) {
  const lock = clean(conceptLock);
  const legalLines = clean(legal);
  if (!lock.length && !legalLines.length) return null;
  return (
    <BriefSection accent="purple">
      <SectionHead accent="purple" title="Rules" />
      <div className="flex flex-col gap-6">
        {lock.length ? (
          <div>
            <FieldLabel>What must not change</FieldLabel>
            {lock.length === 1 ? (
              <p className="mt-1.5 whitespace-pre-wrap text-[15px] leading-[1.5] text-co-text2">{lock[0]}</p>
            ) : (
              <BulletList items={lock} className="mt-2.5" />
            )}
          </div>
        ) : null}
        {legalLines.length ? (
          <div>
            <LegalLines lines={legalLines} />
          </div>
        ) : null}
      </div>
    </BriefSection>
  );
}
